import { Outlet, useParams } from 'react-router-dom';
import PATH_PAGE from '@/router/path';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import useDemoPath from '@/hook/useDemoPath';
import { useMemo } from 'react';
import useConsoleLayoutConfig from '@/hook/useConsoleLayoutConfig';
import { ArrowLeftIcon } from '@radix-ui/react-icons';
import { useListSSHInstances } from '@/service/gpu-instance/hook';
import { useDemoMode } from '@/context/DemoModeContext';
import { Database, Upload, Shield, Zap, Target, BarChart3, Brain, HeartPulse, ScanText, Code2, ScrollText, MessageSquare, Globe } from 'lucide-react';

const GPUInstanceDetailLayout = () => {
  const { orgId, workspaceId } = useParamWorkspaceId();
  const { instanceId } = useParams<{ instanceId: string }>();
  const { data: instances } = useListSSHInstances();
  const { isDemoMode } = useDemoMode();
  const { withDemoPrefix } = useDemoPath();

  const instance = instances?.find((i) => i.task_id === instanceId);
  const instanceTag = instance?.instance_tag;

  // Helper to check if tag matches (handles both old and new formats)
  const isTagType = (type: 'vllm' | 'triton' | 'tao' | 'monai' | 'nemo' | 'float16' | 'ocr') => {
    if (!instanceTag) return false;
    return instanceTag === type || instanceTag === `ssh-${type}` ||
           (type === 'float16' && instanceTag === 'float16-training');
  };

  const navItems = useMemo(() => {
    if (!instanceId) return [];

    const baseItems = [
      {
        href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.instances(orgId, workspaceId)),
        label: 'All Instances',
        strictMatch: true,
        separator: true,
        icon: <ArrowLeftIcon />,
      },
      {
        href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).overview),
        label: 'Overview',
        strictMatch: true,
      },
      {
        href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).logs),
        label: 'Logs',
        strictMatch: true,
        icon: <ScrollText className="h-4 w-4" />,
      },
      {
        href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).endpoint),
        label: 'Endpoint',
        strictMatch: true,
        icon: <Globe className="h-4 w-4" />,
      },
    ];

    // Add Triton-specific nav items
    if (isDemoMode && isTagType('triton')) {
      baseItems.push(
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).modelRepository),
          label: 'Model Repository',
          strictMatch: true,
          separator: false,
          icon: <Database className="h-4 w-4" />,
        },
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).modelManagement),
          label: 'Model Management',
          strictMatch: true,
          separator: false,
          icon: <Upload className="h-4 w-4" />,
        },
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).modelTesting),
          label: 'Model Testing',
          strictMatch: true,
          separator: false,
          icon: <Zap className="h-4 w-4" />,
        },
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).botPrevention),
          label: 'Bot Prevention',
          strictMatch: true,
          separator: false,
          icon: <Shield className="h-4 w-4" />,
        }
      );
    }

    // Add vLLM-specific nav items
    if (isTagType('vllm')) {
      baseItems.push(
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).playground),
          label: 'Playground',
          strictMatch: true,
          separator: false,
          icon: <MessageSquare className="h-4 w-4" />,
        }
      );
      if (isDemoMode) {
        baseItems.push(
          {
            href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).botPrevention),
            label: 'Bot Prevention',
            strictMatch: true,
            separator: false,
            icon: <Shield className="h-4 w-4" />,
          }
        );
      }
    }

    // Add TAO-specific nav items (TAO 6.x Training)
    if (isDemoMode && isTagType('tao')) {
      baseItems.push(
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).taoExperiments),
          label: 'Experiments',
          strictMatch: true,
          separator: false,
          icon: <BarChart3 className="h-4 w-4" />,
        },
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).taoDatasets),
          label: 'Datasets',
          strictMatch: true,
          separator: false,
          icon: <Database className="h-4 w-4" />,
        },
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).taoModels),
          label: 'Model Catalog',
          strictMatch: true,
          separator: false,
          icon: <Brain className="h-4 w-4" />,
        }
      );
    }

    // Add MONAI-specific nav items (MONAI 1.5.1 Training)
    if (isDemoMode && isTagType('monai')) {
      baseItems.push(
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).monaiExperiments),
          label: 'Experiments',
          strictMatch: true,
          separator: false,
          icon: <BarChart3 className="h-4 w-4" />,
        },
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).monaiDatasets),
          label: 'Datasets',
          strictMatch: true,
          separator: false,
          icon: <Database className="h-4 w-4" />,
        },
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).monaiModels),
          label: 'Model Catalog',
          strictMatch: true,
          separator: false,
          icon: <Brain className="h-4 w-4" />,
        }
      );
    }

    // Add NeMo-specific nav items (NeMo 2.6.1 Training)
    if (isDemoMode && isTagType('nemo')) {
      baseItems.push(
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).nemoExperiments),
          label: 'Experiments',
          strictMatch: true,
          separator: false,
          icon: <BarChart3 className="h-4 w-4" />,
        },
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).nemoDatasets),
          label: 'Datasets',
          strictMatch: true,
          separator: false,
          icon: <Database className="h-4 w-4" />,
        },
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).nemoModels),
          label: 'Model Catalog',
          strictMatch: true,
          separator: false,
          icon: <Brain className="h-4 w-4" />,
        }
      );
    }

    // Add OCR-specific nav items (Thai Document OCR)
    if (isDemoMode && isTagType('ocr')) {
      baseItems.push(
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).ocrPlayground),
          label: 'OCR Playground',
          strictMatch: true,
          separator: false,
          icon: <ScanText className="h-4 w-4" />,
        },
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).ocrApiEndpoint),
          label: 'API Endpoint',
          strictMatch: true,
          separator: false,
          icon: <Code2 className="h-4 w-4" />,
        },
        {
          href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).ocrMetrics),
          label: 'Metrics',
          strictMatch: true,
          separator: false,
          icon: <BarChart3 className="h-4 w-4" />,
        }
      );
    }

    return baseItems;
  }, [orgId, workspaceId, instanceId, instanceTag, isDemoMode, withDemoPrefix]);

  // Get the original/parent task_id for display
  const displayInstanceId = instance?.parent_task_id || instance?.task_id || instanceId;

  const navbarConfig = useMemo(
    () => ({
      title: `Instance: ${displayInstanceId?.slice(0, 8)}...`,
      navbar: navItems,
    }),
    [displayInstanceId, navItems]
  );

  useConsoleLayoutConfig({
    navConfig: navbarConfig,
  });

  return <Outlet />;
};

export default GPUInstanceDetailLayout;
